import React, { Suspense, lazy, useEffect, useState, useRef } from "react";
import {
  createBrowserRouter,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import appRouter from "./appRouter";
import adminRouter from "./adminRouter";
import useCustomLogin from "../hooks/useCustomLogin";
import { getAccessToken } from "../util/jwtUtil";

const Loading = () => (
  <div className="flex justify-center items-center h-screen w-full">
    <div className="animate-spin border-t-2 border-b-2 border-blue-500 rounded-full w-8 h-8"></div>
    <span className="ml-2">Loading...</span>
  </div>
);

const NotFound = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate("/", { replace: true });
  }, [navigate]);
};

const Login = lazy(() => import("../pages/LoginPage"));
const TwoFactorVerify = lazy(() => import("../pages/TwoFactorVerifyPage"));
const TwoFactorSetup = lazy(() => import("../pages/TwoFactorSetupPage"));
const Guide = lazy(() => import("../pages/GuidePage"));
const AdminGuide = lazy(() => import("../pages/AdminGuidePage"));
const AppIndex = lazy(() => import("../pages/app/AppIndex"));
const AdminIndex = lazy(() => import("../pages/admin/AdminIndex"));
const MemberBrowseStandalone = lazy(() =>
  import("../pages/member/MemberBrowseStandalone")
);
const MemberGameApp = lazy(() => import("../pages/member/MemberGameApp"));

const getDashboardPath = (roleNames) => {
  if (roleNames.includes("SUPER") || roleNames.includes("ADMIN")) {
    return "/admin/";
  }
  return "/app/";
};

const RequireAuth = ({ children, isAdminRoute }) => {
  const { isLogin, loginInfo, refreshToken, doLogout } = useCustomLogin();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const authCheckDone = useRef(false);
  const redirectAttempted = useRef(false);

  useEffect(() => {
    const checkAuth = async () => {
      // 이미 인증 체크를 완료했으면 스킵
      if (authCheckDone.current) {
        setLoading(false);
        return;
      }

      // 메모리에 토큰이 있으면 로그인된 상태로 간주
      if (getAccessToken()) {
        authCheckDone.current = true;
        setLoading(false);
        return;
      }

      // Redux에 토큰이 있으면 로그인된 상태로 간주
      if (isLogin) {
        authCheckDone.current = true;
        setLoading(false);
        return;
      }

      // 토큰이 없으면 리프레시 시도
      const refreshed = await refreshToken();
      if (!refreshed) {
        // 리프레시 실패시에만 로그아웃
        doLogout();
        return;
      }
      authCheckDone.current = true;
      setLoading(false);
    };
    checkAuth();
  }, [isLogin, refreshToken, doLogout]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen w-full">
        <div className="animate-spin border-t-2 border-b-2 border-blue-500 rounded-full w-8 h-8"></div>
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  // 메모리에 토큰이 있으면 로그인된 상태로 간주 (Redux 상태 업데이트 지연 대응)
  const memoryToken = getAccessToken();
  const hasMemoryToken = !!memoryToken;

  // 로그인 상태가 아니고 메모리 토큰도 없으면 로그인 페이지로 리다이렉트
  // 단, 리다이렉트는 한 번만 시도 (중복 방지)
  if (!isLogin && !hasMemoryToken && !redirectAttempted.current) {
    redirectAttempted.current = true;
    if (location.pathname.startsWith("/app") || location.pathname.startsWith("/admin")) {
      const currentPath = encodeURIComponent(
        location.pathname + location.search
      );
      return <Navigate to={`/login?returnUri=${currentPath}`} replace />;
    } else {
      return <Navigate to="/login" replace />;
    }
  }

  // 리다이렉트가 이미 시도되었지만 아직 토큰이 없으면 로딩 표시
  if (!isLogin && !hasMemoryToken) {
    return (
      <div className="flex justify-center items-center h-screen w-full">
        <div className="animate-spin border-t-2 border-b-2 border-blue-500 rounded-full w-8 h-8"></div>
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  // loginInfo 결정: Redux에서 가져오거나, 메모리 토큰에서 추출
  let effectiveLoginInfo = loginInfo;
  if (!effectiveLoginInfo && hasMemoryToken) {
    try {
      const base64Url = memoryToken.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      effectiveLoginInfo = JSON.parse(jsonPayload);
    } catch (e) {
      console.error("Failed to parse token:", e);
    }
  }

  // loginInfo가 여전히 없으면 로딩 상태 유지
  if (!effectiveLoginInfo) {
    return (
      <div className="flex justify-center items-center h-screen w-full">
        <div className="animate-spin border-t-2 border-b-2 border-blue-500 rounded-full w-8 h-8"></div>
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  if (isAdminRoute) {
    const allowedRoles = ["SUPER", "ADMIN", "MANAGER"];
    const userRoles = effectiveLoginInfo.roleNames || [];

    const hasAccess = userRoles.some((role) => allowedRoles.includes(role));

    if (!hasAccess) {
      const dashboardPath = getDashboardPath(userRoles);
      return <Navigate to={dashboardPath} state={{ from: location }} replace />;
    }
  }

  const dashboardPath = getDashboardPath(effectiveLoginInfo.roleNames || []);
  if (location.pathname === "/") {
    return <Navigate to={dashboardPath} state={{ from: location }} replace />;
  }

  return children;
};

const InitLoginPage = ({ children }) => {
  const { initLogin } = useCustomLogin();
  useEffect(() => {
    // 이미 토큰이 있으면 초기화하지 않음 (2FA 인증 후 리다이렉트 방지)
    if (!getAccessToken()) {
      initLogin();
    }
  }, []);
  return children;
};

const root = createBrowserRouter(
  [
    {
      path: "login",
      element: (
        <InitLoginPage>
          <Suspense fallback={<Loading />}>
            <Login />
          </Suspense>
        </InitLoginPage>
      ),
    },
    {
      path: "verify-2fa",
      element: (
        <Suspense fallback={<Loading />}>
          <TwoFactorVerify />
        </Suspense>
      ),
    },
    {
      path: "setup-2fa",
      element: (
        <Suspense fallback={<Loading />}>
          <TwoFactorSetup />
        </Suspense>
      ),
    },
    {
      path: "/",
      element: (
        <RequireAuth>
          <Suspense fallback={<Loading />}>
            <Navigate to="/app/performance/dashboard" replace />
          </Suspense>
        </RequireAuth>
      ),
    },
    {
      path: "app",
      element: (
        <RequireAuth isAdminRoute={false}>
          <Suspense fallback={<Loading />}>
            <AppIndex />
          </Suspense>
        </RequireAuth>
      ),
      children: appRouter(),
    },
    {
      path: "admin",
      element: (
        <RequireAuth isAdminRoute={true}>
          <Suspense fallback={<Loading />}>
            <AdminIndex />
          </Suspense>
        </RequireAuth>
      ),
      children: adminRouter(),
    },
    {
      path: "guide",
      element: <Guide />,
    },
    {
      path: "admin-guide",
      element: <AdminGuide />,
    },
    // 새로운 독립 페이지들 추가
    {
      path: "member-browse",
      element: (
        <RequireAuth>
          <Suspense fallback={<Loading />}>
            <MemberBrowseStandalone />
          </Suspense>
        </RequireAuth>
      ),
    },
    {
      path: "member-game",
      element: (
        <RequireAuth>
          <Suspense fallback={<Loading />}>
            <MemberGameApp />
          </Suspense>
        </RequireAuth>
      ),
    },
    {
      path: "*",
      element: <NotFound />,
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);

export default root;
